import { Component, ViewChild } from '@angular/core';
import { EventsListGridResponse } from '../../../models/events/eventsAdminGridResponse';
import { NzTabChangeEvent } from 'ng-zorro-antd/tabs';
import {
  EventListSort,
  EventsListTab,
  EventsListsGridRequest,
} from '../../../models/events/eventsGridRequest';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import { EventsService } from '../../../services/events.service';
import { lastValueFrom } from 'rxjs';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import {
  DateFilterEmitData,
  EventsTableComponent,
} from '../events-table/events-table.component';
import * as moment from 'moment';
import { UtilsService } from 'projects/app-core/src/service/utils.service';

@Component({
  selector: 'app-manage-events',
  templateUrl: './manage-events.component.html',
  styleUrl: './manage-events.component.scss',
})
export class ManageEventsComponent {
  totalCount = 0;
  tableData: EventsListGridResponse = {
    data: [],
    filterOptions: {
      category: [],
      paymentTypes: [],
    },
    pageNum: 1,
    pageSize: 10,
    totalPages: 0,
    totalRecords: 0,
  };
  eventsTableQuery: EventsListsGridRequest | undefined;
  selectedTab: number = 0;
  tabs = [EventsListTab.UP_COMING_ADMIN, EventsListTab.ONGOING_ADMIN_CLASSES];
  pageSize = 10;
  eventsSpinner = false;
  @ViewChild('UpcomingTableComponent')
  UpcomingTableComponent?: EventsTableComponent;
  @ViewChild('AllTableComponent')
  AllTableComponent?: EventsTableComponent;
  @ViewChild('OngoingClassesComponent')
  OngoingClassesComponent?: EventsTableComponent;

  constructor(
    private eventsService: EventsService,
    private toastrService: ToastService,
    private auth: AuthService,
    public utils: UtilsService,
  ) { }

  getHideTestEventValue(): string[] {
    let hide = false;
    if (this.selectedTab === 0 && this.UpcomingTableComponent) {
      hide = this.UpcomingTableComponent.hideTestEvent;
    } else if (this.selectedTab === 1 && this.OngoingClassesComponent) {
      hide = this.OngoingClassesComponent.hideTestEvent;
    } else if (this.selectedTab === 2 && this.AllTableComponent) {
      hide = this.AllTableComponent.hideTestEvent;
    }
    return hide ? ['true'] : ['false'];
  }

  async tabChange($event: NzTabChangeEvent) {
    this.selectedTab = $event.index!;
    this.tableData = {
      data: [],
      filterOptions: {
        category: [],
        paymentTypes: [],
      },
      pageNum: 1,
      pageSize: 10,
      totalPages: 0,
      totalRecords: 0,
    };
    this.eventsTableQuery = {
      sort: { EVENT_DATE: AnyoSortDirection.DESC, PRICE: undefined },
      dateFilters: undefined,
      filters: {
        CATEGORY: [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: [],
        TEST_EVENTS: ['false'],
      },
      export: false,
      globalSearch: [],
      page: 1,
      pageSize: this.pageSize,
      tab: this.tabs[this.selectedTab],
    };
    if (this.UpcomingTableComponent) {
      this.UpcomingTableComponent!.resetValues();
      this.UpcomingTableComponent!.resetSearch();
      this.UpcomingTableComponent!.hideTestEvent = false;
    }
    if (this.AllTableComponent) {
      this.AllTableComponent!.resetValues();
      this.AllTableComponent!.resetSearch();
      this.AllTableComponent!.hideTestEvent = false;
    }
    if (this.OngoingClassesComponent) {
      this.OngoingClassesComponent!.resetValues();
      this.OngoingClassesComponent!.resetSearch();
      this.OngoingClassesComponent!.hideTestEvent = false;
    }
    if ($event.index !== undefined) {
      if ($event.index == 0) {
        this.eventsTableQuery!.tab = EventsListTab.UP_COMING_ADMIN;
      }
      if ($event.index == 1) {
        this.eventsTableQuery!.tab = EventsListTab.ONGOING_ADMIN_CLASSES;
      }
      if ($event.index == 2) {
        this.eventsTableQuery!.tab = undefined;
      }
    }
    await this.listEvents();
  }

  async listEvents() {
    try {
      this.eventsSpinner = true;
      const tableData = await lastValueFrom(
        this.eventsService.listEvents(this.eventsTableQuery!),
      );
      tableData.data.forEach((data) => {
        data.paymentTypeColor = this.utils.getRandomColor();
        data.categoryNameColor = data.paymentTypeColor =
          this.utils.getRandomColor();
      });
      this.tableData = tableData;
      this.totalCount = this.tableData.totalRecords;
      if (this.UpcomingTableComponent) {
        this.UpcomingTableComponent.setCategoryFilterData(
          this.tableData.filterOptions,
        );
        this.UpcomingTableComponent.setPaymentTypeFilterData(
          this.tableData.filterOptions,
        );
      }
      if (this.OngoingClassesComponent) {
        this.OngoingClassesComponent.setCategoryFilterData(
          this.tableData.filterOptions,
        );
        this.OngoingClassesComponent.setPaymentTypeFilterData(
          this.tableData.filterOptions,
        );
      }
      if (this.AllTableComponent) {
        this.AllTableComponent.setCategoryFilterData(
          this.tableData.filterOptions,
        );
        this.AllTableComponent.setPaymentTypeFilterData(
          this.tableData.filterOptions,
        );
      }
      this.eventsSpinner = false;
    } catch (e) {
      this.eventsSpinner = false;
      this.toastrService.showError('Something went wrong');
    }
  }

  async categoryFilter($event: string[]) {
    const hideTest = this.getHideTestEventValue();
    this.eventsTableQuery = {
      ...this.eventsTableQuery!,
      dateFilters: this.eventsTableQuery?.dateFilters,
      filters: {
        CATEGORY: $event || [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: this.eventsTableQuery?.filters?.ACTIVE || [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: this.eventsTableQuery?.filters?.PAYMENT_TYPE || [],
        TEST_EVENTS: hideTest,
      },
      page: 1,
    };
    await this.listEvents();
  }

  async paymentTypeFilter($event: string[]) {
    const hideTest = this.getHideTestEventValue();
    this.eventsTableQuery = {
      ...this.eventsTableQuery!,
      dateFilters: this.eventsTableQuery?.dateFilters,
      filters: {
        CATEGORY: this.eventsTableQuery?.filters?.CATEGORY || [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: this.eventsTableQuery?.filters?.ACTIVE || [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: $event || [],
        TEST_EVENTS: hideTest,
      },
      page: 1,
    };
    await this.listEvents();
  }

  async activeFilter($event: string[]) {
    const hideTest = this.getHideTestEventValue();
    this.eventsTableQuery = {
      ...this.eventsTableQuery!,
      dateFilters: this.eventsTableQuery?.dateFilters,
      filters: {
        CATEGORY: this.eventsTableQuery?.filters?.CATEGORY || [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: $event || [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: this.eventsTableQuery?.filters?.PAYMENT_TYPE || [],
        TEST_EVENTS: hideTest,
      },
      page: 1,
    };
    await this.listEvents();
  }

  async testFilter($event: boolean) {
    if (!this.eventsTableQuery) {
      this.eventsTableQuery = {
        dateFilters: undefined,
        filters: {
          CATEGORY: [],
          CIRCLE_ID: [],
          PRESENTER_ID: [],
          ACTIVE: [],
          USER_PERSONALISED: [],
          PAYMENT_TYPE: [],
          TEST_EVENTS: $event ? ['true'] : ['false'],
        },
        export: false,
        globalSearch: [],
        page: 1,
        pageSize: this.pageSize,
        tab: this.tabs[this.selectedTab],
      };
    } else {
      this.eventsTableQuery.filters = {
        CATEGORY: this.eventsTableQuery?.filters?.CATEGORY || [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: this.eventsTableQuery?.filters?.ACTIVE || [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: this.eventsTableQuery?.filters?.PAYMENT_TYPE || [],
        TEST_EVENTS: $event ? ['true'] : ['false'],
      };
    }
    await this.listEvents();
  }

  async dateFilter($event: DateFilterEmitData) {
    console.log($event);
    const hideTest = this.getHideTestEventValue();
    if ($event.dates && $event.dates.length) {
      const startDate = moment($event.dates[0]).startOf('day').toDate();
      const endDate = moment($event.dates[1]).endOf('day').toDate();
      
      let dateFilters: any = {
        EVENT_DATE: undefined,
        REGISTRATION_CUT_OFF: undefined,
        VISIBLE_FROM: undefined,
        CREATED_DATE: undefined,
      };

      if ($event.options.includes('eventDate')) {
        dateFilters.EVENT_DATE = { from: startDate, to: endDate };
      } else if ($event.options.includes('registrationCutOffDate')) {
        dateFilters.REGISTRATION_CUT_OFF = { from: startDate, to: endDate };
      } else if ($event.options.includes('visibleFrom')) {
        dateFilters.VISIBLE_FROM = { from: startDate, to: endDate };
      }

      this.eventsTableQuery = {
        ...this.eventsTableQuery!,
        dateFilters: dateFilters,
        filters: {
          CATEGORY: this.eventsTableQuery?.filters?.CATEGORY || [],
          CIRCLE_ID: [],
          PRESENTER_ID: [],
          ACTIVE: this.eventsTableQuery?.filters?.ACTIVE || [],
          USER_PERSONALISED: [],
          PAYMENT_TYPE: this.eventsTableQuery?.filters?.PAYMENT_TYPE || [],
          TEST_EVENTS: hideTest,
        },
        page: 1,
      };
    } else {
      this.eventsTableQuery = {
        ...this.eventsTableQuery!,
        dateFilters: undefined,
        filters: {
          CATEGORY: this.eventsTableQuery?.filters?.CATEGORY || [],
          CIRCLE_ID: [],
          PRESENTER_ID: [],
          ACTIVE: this.eventsTableQuery?.filters?.ACTIVE || [],
          USER_PERSONALISED: [],
          PAYMENT_TYPE: this.eventsTableQuery?.filters?.PAYMENT_TYPE || [],
          TEST_EVENTS: hideTest,
        },
        page: 1,
      };
    }
    await this.listEvents();
  }

  async nzPageSizeChange(pageSize: number) {
    this.eventsTableQuery!.pageSize = pageSize;
    await this.listEvents();
  }

  async nzPageIndexChange(page: number) {
    if (this.eventsTableQuery) {
      this.eventsTableQuery!.page = page;
      await this.listEvents();
    }
  }

  async resetFilter() {
    this.eventsTableQuery = {
      export: false,
      sort: { EVENT_DATE: AnyoSortDirection.DESC, PRICE: undefined },
      globalSearch: [],
      page: 1,
      pageSize: this.pageSize,
      tab: this.tabs[this.selectedTab],
      filters: {
        CATEGORY: [],
        CIRCLE_ID: [],
        PRESENTER_ID: [],
        ACTIVE: [],
        USER_PERSONALISED: [],
        PAYMENT_TYPE: [],
        TEST_EVENTS: ['false'],
      },
    };
    if (this.UpcomingTableComponent) {
      this.UpcomingTableComponent!.resetValues();
      this.UpcomingTableComponent!.resetSearch();
      this.UpcomingTableComponent!.hideTestEvent = false;
    }
    if (this.AllTableComponent) {
      this.AllTableComponent!.resetValues();
      this.AllTableComponent!.resetSearch();
      this.AllTableComponent!.hideTestEvent = false;
    }
    if (this.OngoingClassesComponent) {
      this.OngoingClassesComponent!.resetValues();
      this.OngoingClassesComponent!.resetSearch();
      this.OngoingClassesComponent!.hideTestEvent = false;
    }
    await this.listEvents();
  }

  async searchGlobal(searchTerm: string) {
    if (searchTerm.length) {
      this.eventsTableQuery = {
        ...this.eventsTableQuery!,
        globalSearch: [searchTerm],
        page: 1,
      };
      await this.listEvents();
    }
  }

  ngOnInit() {
    this.auth.currentAuthStatus.subscribe(async (user) => {
      if (user) {
        this.eventsTableQuery = {
          sort: { EVENT_DATE: AnyoSortDirection.DESC, PRICE: undefined },
          dateFilters: undefined,
          export: false,
          filters: {
            CATEGORY: [],
            CIRCLE_ID: [],
            PRESENTER_ID: [],
            ACTIVE: [],
            USER_PERSONALISED: [],
            PAYMENT_TYPE: [],
            TEST_EVENTS: ['false'],
          },
          from: undefined,
          globalSearch: [],
          page: 1,
          pageSize: this.pageSize,
          searchQuery: undefined,
          tab: EventsListTab.UP_COMING_ADMIN,
          to: undefined,
        };
        await this.listEvents();
      }
    });
  }
}
